// ============================================================================
// Issues & CRM notes — what was escalated on this order and what the CRM team
// wrote back. Notes are read live from Kylas, so a thread can be partially
// unavailable without the rest of the page failing.
//
// NOT the order page's Escalations section: that one is post-placement
// mutations (cancel / short supply / return) and their Zoho handling. This is
// the customer-facing story — "what actually happened with this order".
//
// Opened from a header button into a side drawer, so the order page keeps the
// shape it had and nothing is fetched until somebody asks.
// ============================================================================
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  ExportOutlined,
  MessageOutlined,
  WarningFilled,
} from "@ant-design/icons";
import { Alert, Button, Collapse, Drawer, Empty, Input, Space, Spin, Tag, Timeline, Tooltip, Typography } from "antd";
import dayjs from "dayjs";
import React, { useCallback, useEffect, useState } from "react";
import { addCrmNote, getCrmIssues } from "../oms.api";
import { CrmIssueEntry, CrmIssueThread as Thread } from "../types";

const ts = (at?: string | null) => (at ? dayjs(at).format("DD MMM YYYY, h:mm A") : "—");
const who = (by?: string | null) => by || "CRM user";

const KIND_DOT: Record<string, { color: string; icon: React.ReactNode }> = {
  issue_raised: { color: "red", icon: <WarningFilled /> },
  issue_resolved: { color: "green", icon: <CheckCircleFilled /> },
  note: { color: "gray", icon: <MessageOutlined /> },
};

const EntryBody: React.FC<{ entry: CrmIssueEntry }> = ({ entry }) => {
  if (entry.kind === "note") {
    return (
      <>
        {/* No per-note ticket link — every note in a panel is on the same deal, already linked
            once at the panel header. */}
        <Typography.Text strong>{who(entry.author)}</Typography.Text>{" "}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          commented · {ts(entry.at)}
          {entry.edited_at ? " (edited)" : ""}
        </Typography.Text>
        <div style={{ whiteSpace: "pre-wrap", marginTop: 2 }}>{entry.text}</div>
      </>
    );
  }

  const raised = entry.kind === "issue_raised";
  return (
    <>
      <Space size={4} wrap>
        <Typography.Text strong>{raised ? "Issue raised" : "Issue resolved"}</Typography.Text>
        {entry.issue_type && <Tag color={raised ? "red" : "green"}>{entry.issue_type}</Tag>}
        {entry.booking_id && <Tag>booking #{entry.booking_id}</Tag>}
        {(entry.po_numbers || []).map((po) => (
          <Tag key={po}>{po}</Tag>
        ))}
      </Space>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: 0 }}>
        by {entry.by || "the system"} · {ts(entry.at)}
      </Typography.Paragraph>
      {entry.remarks && (
        <div style={{ whiteSpace: "pre-wrap", marginTop: 2 }}>{entry.remarks}</div>
      )}
      {!!entry.groups_failed?.length && (
        // The teams this was meant to alert and did not reach — the one thing on a raise that
        // still needs a human.
        <Tooltip title="These WhatsApp groups did not get the alert">
          <Tag icon={<ExclamationCircleFilled />} color="error" style={{ marginTop: 4 }}>
            not delivered to {entry.groups_failed.join(", ")}
          </Tag>
        </Tooltip>
      )}
    </>
  );
};

const NoteBox: React.FC<{ soId: number; dealId: string; onPosted: (t: Thread) => void }> = ({
  soId,
  dealId,
  onPosted,
}) => {
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);

  const post = async () => {
    const body = text.trim();
    if (!body) return;
    setPosting(true);
    const next = await addCrmNote(soId, dealId, body);
    setPosting(false);
    if (next) {
      setText("");
      onPosted(next);
    }
  };

  return (
    <div style={{ marginTop: 8 }}>
      <Input.TextArea
        rows={2}
        value={text}
        maxLength={1000}
        onChange={(e) => setText(e.target.value)}
        placeholder="Add a note — it goes onto the CRM ticket, so the sales team sees it too"
      />
      <div style={{ textAlign: "right", marginTop: 6 }}>
        <Button type="primary" loading={posting} disabled={!text.trim()} onClick={post}>
          Add note
        </Button>
      </div>
    </div>
  );
};

interface Props {
  salesOrderId: number;
  /** Renders without the surrounding heading — for the logistics drawer, which has its own. */
  compact?: boolean;
}

const CrmIssueThreadView: React.FC<Props> = ({ salesOrderId, compact }) => {
  const [thread, setThread] = useState<Thread | undefined>();
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setThread(await getCrmIssues(salesOrderId));
    setLoading(false);
  }, [salesOrderId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <Spin size="small" />;
  if (!thread) return null;

  if (!thread.results.length) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="No issues raised on this order"
        style={{ margin: compact ? 8 : 16 }}
      />
    );
  }

  // An order can carry several escalations, each its own CRM ticket with its own conversation — so
  // they are never merged into one stream. A note has to name the deal it goes on anyway.
  const notesFor = (dealId: string) =>
    thread.results.filter((e) => e.kind === "note" && e.deal_id === dealId);

  const localEvents = thread.results.filter(
    (e) => e.kind === "issue_raised" || e.kind === "issue_resolved"
  );

  return (
    <>
      {!thread.crm_available && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="The CRM did not answer — notes below may be incomplete"
          description={thread.crm_error}
        />
      )}

      {!!thread.tickets.length && (
        <Collapse
          size="small"
          // Newest first, and the newest one starts open — it is the live conversation.
          defaultActiveKey={[thread.tickets[thread.tickets.length - 1].deal_id]}
          items={[...thread.tickets].reverse().map((ticket) => {
            const notes = notesFor(ticket.deal_id);
            return {
              key: ticket.deal_id,
              label: (
                <Space size={6} wrap>
                  <Typography.Text strong style={{ fontSize: 13 }}>
                    {ticket.title || `Ticket ${ticket.deal_id}`}
                  </Typography.Text>
                  <Tag color={notes.length ? "blue" : "default"}>
                    {notes.length} note{notes.length === 1 ? "" : "s"}
                  </Tag>
                  {ticket.stage && (
                    <Tag color={/resolved|won|closed/i.test(ticket.stage) ? "green" : "orange"}>
                      {ticket.stage}
                    </Tag>
                  )}
                  <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                    {ts(ticket.opened_at)} · owner {who(ticket.owner)}
                  </Typography.Text>
                </Space>
              ),
              extra: (
                <a href={ticket.deal_url} target="_blank" rel="noreferrer"
                   onClick={(e) => e.stopPropagation()}>
                  <ExportOutlined />
                </a>
              ),
              children: (
                <>
                  {ticket.opened_by && (
                    <Typography.Text type="secondary" style={{ fontSize: 11, display: "block", marginBottom: 8 }}>
                      Raised by {ticket.opened_by}
                    </Typography.Text>
                  )}
                  {ticket.notes_error ? (
                    <Alert type="warning" showIcon message={ticket.notes_error} />
                  ) : notes.length ? (
                    <Timeline
                      items={notes.map((n, i) => ({
                        key: n.note_id || i,
                        color: "gray",
                        dot: <MessageOutlined />,
                        children: <EntryBody entry={n} />,
                      }))}
                    />
                  ) : (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      No notes on this ticket yet.
                    </Typography.Text>
                  )}
                  <NoteBox soId={salesOrderId} dealId={ticket.deal_id} onPosted={setThread} />
                </>
              ),
            };
          })}
        />
      )}

      {/* Our own raise/resolve log is normally redundant — the CRM ticket's first note is a copy of
          the raise. It is shown ONLY when the order has no ticket at all, which happens when the
          Kylas deal creation failed (it is fire-and-forget). Without this the drawer would say
          "no issues raised" about an order somebody did escalate. */}
      {!thread.tickets.length && !!localEvents.length && (
        <Timeline
          items={localEvents.map((entry, i) => ({
            key: i,
            color: KIND_DOT[entry.kind]?.color,
            dot: KIND_DOT[entry.kind]?.icon,
            children: <EntryBody entry={entry} />,
          }))}
        />
      )}
    </>
  );
};

// The order page's entry point: a side drawer behind a header button, so the page keeps its shape
// and the thread is only fetched when somebody asks for it. `destroyOnClose` means each open is a
// fresh read — Kylas notes change while the page is sitting there.
export const CrmIssueDrawer: React.FC<{
  salesOrderId: number;
  open: boolean;
  onClose: () => void;
}> = ({ salesOrderId, open, onClose }) => (
  <Drawer
    title="Escalation notes"
    placement="right"
    width={520}
    open={open}
    onClose={onClose}
    destroyOnClose
  >
    <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
      Escalations raised on this order and the CRM team's follow-up. Notes are read live from the
      CRM, and anything you add here lands on the same ticket the sales team sees.
    </Typography.Paragraph>
    <CrmIssueThreadView salesOrderId={salesOrderId} compact />
  </Drawer>
);

export default CrmIssueThreadView;
